import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { eur, frDate } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Phone, MapPin, FileText, Tag, Plus, Pencil, AlertTriangle, Save, Building2 } from "lucide-react";

const TYPES = [["prospect", "Prospect"], ["client", "Client"], ["fournisseur", "Fournisseur"]];

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [savedSnap, setSavedSnap] = useState(null);
  const [saving, setSaving] = useState(false);
  const [coResults, setCoResults] = useState([]);
  const [coSearching, setCoSearching] = useState(false);
  const coTimer = useRef(null);

  const load = () => api.get(`/contacts/${id}`).then(({ data }) => setData(data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const snap = (f) => JSON.stringify([f?.name, f?.email, f?.phone, f?.address, f?.type, f?.kind, f?.siret, f?.vat_number, f?.tags, f?.notes]);
  const dirty = !!form && editOpen && savedSnap !== null && snap(form) !== savedSnap;
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const openEdit = () => {
    const c = data.contact;
    const f = { name: c.name || "", email: c.email || "", phone: c.phone || "", address: c.address || "", type: c.type || "prospect", kind: c.kind || "particulier", siret: c.siret || "", vat_number: c.vat_number || "", company_name: c.company_name || "", tags: (c.tags || []).join(", "), notes: c.notes || "" };
    setForm(f); setSavedSnap(snap(f)); setCoResults([]); setEditOpen(true);
  };

  const searchCompany = (v) => {
    setForm((f) => ({ ...f, name: v }));
    if (coTimer.current) clearTimeout(coTimer.current);
    if ((v || "").trim().length < 2) { setCoResults([]); return; }
    coTimer.current = setTimeout(async () => {
      setCoSearching(true);
      try { const { data } = await api.get("/company-search", { params: { q: v } }); setCoResults(data || []); }
      catch { setCoResults([]); }
      finally { setCoSearching(false); }
    }, 350);
  };
  const pickCompany = (co) => {
    setForm((f) => ({ ...f, name: co.name, company_name: co.name, siret: co.siret || "", vat_number: co.vat_number || "", address: co.address || f.address }));
    setCoResults([]);
  };

  const closeGuard = (o) => {
    if (o) { setEditOpen(true); return; }
    if (dirty && !window.confirm("Vous avez des modifications non enregistrées. Quitter sans enregistrer ?")) return;
    setEditOpen(false);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Le nom est requis");
    setSaving(true);
    try {
      const c = data.contact;
      await api.put(`/contacts/${id}`, {
        name: form.name, email: form.email, phone: form.phone, address: form.address, type: form.type,
        kind: form.kind, siret: form.siret, vat_number: form.vat_number, company_name: form.company_name,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [], notes: form.notes,
        pipeline_stage: c.pipeline_stage || "nouveau_lead", lost_reason: c.lost_reason || "",
      });
      toast.success("Contact enregistré");
      setSavedSnap(snap(form)); setEditOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="text-zinc-500">Chargement…</div>;
  const c = data.contact;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-zinc-500">
        <Link to="/app/contacts" className="hover:text-white">Contacts</Link>
        <span>/</span><span className="text-white">{c.name}</span>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#FF6A00]/30 to-[#FFD500]/20 font-display text-2xl font-bold text-white">{c.name.slice(0, 1).toUpperCase()}</div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-2xl font-bold text-white">{c.name}</h1>
              {c.kind === "pro"
                ? <span data-testid="contact-pro-badge" className="flex items-center gap-1 rounded-full border border-[#FF6A00]/40 bg-[#FF6A00]/10 px-2.5 py-0.5 text-xs font-medium text-[#FF6A00]"><Building2 className="h-3 w-3" />Pro</span>
                : <span data-testid="contact-particulier-badge" className="rounded-full border border-zinc-700 px-2.5 py-0.5 text-xs text-zinc-400">Particulier</span>}
            </div>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-zinc-400">
              {c.email && <span className="flex items-center gap-1.5"><Mail className="h-4 w-4" />{c.email}</span>}
              {c.phone && <span className="flex items-center gap-1.5"><Phone className="h-4 w-4" />{c.phone}</span>}
              {c.address && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{c.address}</span>}
              {c.kind === "pro" && c.siret && <span className="flex items-center gap-1.5" data-testid="contact-siret-display"><Building2 className="h-4 w-4" />SIRET {c.siret}{c.vat_number ? ` · TVA ${c.vat_number}` : ""}</span>}
            </div>
            {(c.tags || []).length > 0 && (
              <div className="mt-2 flex gap-1">
                {c.tags.map((t) => <span key={t} className="flex items-center gap-1 rounded-full bg-zinc-800 px-2 py-0.5 text-xs text-zinc-400"><Tag className="h-3 w-3" />{t}</span>)}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="contact-edit" onClick={openEdit} variant="outline" className="rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800"><Pencil className="mr-1 h-4 w-4" />Modifier</Button>
          <Button data-testid="contact-new-quote" onClick={() => navigate(`/app/devis/nouveau?contact=${id}`)} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Plus className="mr-1 h-4 w-4" />Créer un devis</Button>
        </div>
      </div>

      {c.notes && (
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
          <h2 className="mb-2 font-display text-lg font-semibold text-white">Notes</h2>
          <p className="whitespace-pre-wrap text-sm text-zinc-400">{c.notes}</p>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
          <h2 className="mb-4 font-display text-lg font-semibold text-white">Devis liés</h2>
          {data.quotes.length === 0 ? <p className="text-sm text-zinc-500">Aucun devis.</p> : (
            <div className="space-y-2">
              {data.quotes.map((q) => (
                <div key={q.id} onClick={() => navigate(`/app/devis/${q.id}`)} className="flex cursor-pointer items-center justify-between rounded-xl border border-zinc-800 p-3 hover:border-zinc-700">
                  <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-zinc-500" /><span className="text-sm text-white">{q.number}</span></div>
                  <div className="flex items-center gap-3"><span className="text-sm text-zinc-400">{eur(q.total)}</span><StatusBadge status={q.status} type="quote" /></div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
          <h2 className="mb-4 font-display text-lg font-semibold text-white">Événements</h2>
          {data.events.length === 0 ? <p className="text-sm text-zinc-500">Aucun événement.</p> : (
            <div className="space-y-2">
              {data.events.map((e) => (
                <div key={e.id} className="flex items-center justify-between rounded-xl border border-zinc-800 p-3">
                  <div><p className="text-sm text-white">{e.title}</p><p className="text-xs text-zinc-500">{frDate(e.date)}</p></div>
                  <StatusBadge status={e.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={closeGuard}>
        <DialogContent className="border-zinc-800 bg-zinc-950" data-testid="contact-edit-dialog">
          <DialogHeader><DialogTitle className="text-white">Modifier le contact</DialogTitle><DialogDescription className="text-zinc-400">Mettez à jour les informations de ce contact.</DialogDescription></DialogHeader>
          {form && (
            <div className="space-y-3">
              {dirty && (
                <div data-testid="contact-unsaved-banner" className="flex items-center justify-between gap-3 rounded-xl border border-[#FFD500]/40 bg-[#1a1710] px-4 py-2.5 text-sm text-[#FFD500]">
                  <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 shrink-0" />Modifications non enregistrées</span>
                </div>
              )}
              <div className="flex gap-2" data-testid="contact-edit-kind-toggle">
                <button type="button" data-testid="contact-edit-kind-particulier" onClick={() => setForm({ ...form, kind: "particulier", siret: "", vat_number: "", company_name: "" })} className={`flex-1 rounded-full border px-3 py-2 text-sm font-medium transition-colors ${form.kind !== "pro" ? "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00]" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>Particulier</button>
                <button type="button" data-testid="contact-edit-kind-pro" onClick={() => setForm({ ...form, kind: "pro" })} className={`flex-1 rounded-full border px-3 py-2 text-sm font-medium transition-colors ${form.kind === "pro" ? "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00]" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>Professionnel (SIRET)</button>
              </div>
              {form.kind === "pro" ? (
                <div className="relative">
                  <Label className="text-zinc-400">Entreprise *</Label>
                  <Input data-testid="contact-edit-company-search" className="mt-1 border-zinc-800 bg-zinc-900" placeholder="Tapez le nom — recherche base nationale…" value={form.name} onChange={(e) => searchCompany(e.target.value)} />
                  {coSearching && <p className="mt-1 text-xs text-zinc-500" data-testid="contact-edit-company-loading">Recherche…</p>}
                  {coResults.length > 0 && (
                    <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-zinc-800 bg-zinc-900 shadow-xl" data-testid="contact-edit-company-results">
                      {coResults.map((co) => (
                        <button type="button" key={co.siret || co.siren} data-testid={`contact-edit-company-opt-${co.siret || co.siren}`} onClick={() => pickCompany(co)} className="block w-full border-b border-zinc-800/60 px-3 py-2 text-left text-sm hover:bg-[#FF6A00]/10">
                          <span className="font-medium text-white">{co.name}</span>
                          <span className="block text-xs text-zinc-500">SIRET {co.siret || "—"} · {co.address || ""}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div><Label className="text-zinc-400">Nom *</Label><Input data-testid="contact-edit-name" className="mt-1 border-zinc-800 bg-zinc-900" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              )}
              {form.kind === "pro" && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-zinc-400">SIRET</Label><Input data-testid="contact-edit-siret" className="mt-1 border-zinc-800 bg-zinc-900" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} /></div>
                  <div><Label className="text-zinc-400">N° TVA</Label><Input data-testid="contact-edit-vat" className="mt-1 border-zinc-800 bg-zinc-900" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-zinc-400">Email</Label><Input data-testid="contact-edit-email" className="mt-1 border-zinc-800 bg-zinc-900" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label className="text-zinc-400">Téléphone</Label><Input data-testid="contact-edit-phone" className="mt-1 border-zinc-800 bg-zinc-900" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div><Label className="text-zinc-400">Adresse</Label><Input data-testid="contact-edit-address" className="mt-1 border-zinc-800 bg-zinc-900" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div>
                <Label className="text-zinc-400">Type</Label>
                <select data-testid="contact-edit-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white">
                  {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div><Label className="text-zinc-400">Tags (séparés par des virgules)</Label><Input data-testid="contact-edit-tags" className="mt-1 border-zinc-800 bg-zinc-900" placeholder="VIP, Recommandé" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></div>
              <div><Label className="text-zinc-400">Notes</Label><Textarea data-testid="contact-edit-notes" className="mt-1 border-zinc-800 bg-zinc-900" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <Button data-testid="contact-edit-save" onClick={save} disabled={saving || !dirty} className="w-full rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00] disabled:opacity-50"><Save className="mr-1.5 h-4 w-4" />Enregistrer</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
